<script>
import { getSettlementList } from '@/api/settlement.js'
import AppPagination from '@/components/common/AppPagination.vue'

const todayText = () => new Date().toISOString().substring(0, 10)
const monthAgoText = () => {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().substring(0, 10)
}

export default {
  name: 'SettlementListPage',
  components: { AppPagination },
  data() {
    return {
      search: {
        keyword: '',
        partyType: '',
        settlementType: '',
        settlementStatus: '',
        startDate: monthAgoText(),
        endDate: todayText(),
      },
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      partyTypes: [
        { title: '전체', value: '' },
        { title: '본사', value: 'HQ' },
        { title: '지점', value: 'BRANCH' },
        { title: '위탁자', value: 'CONSIGNOR' },
        { title: 'PG사', value: 'PG' },
        { title: '배송사', value: 'DELIVERY' },
      ],
      settlementTypes: [
        { title: '전체', value: '' },
        { title: '상품 판매금', value: 'PRODUCT_SALE' },
        { title: '임가공비', value: 'PROCESSING_FEE' },
        { title: '배송비', value: 'SHIPPING_FEE' },
        { title: '카드 수수료', value: 'CARD_FEE' },
        { title: '지점 수수료', value: 'BRANCH_COMMISSION' },
        { title: '위탁 정산금', value: 'CONSIGNMENT_PAYABLE' },
        { title: '환불', value: 'REFUND' },
        { title: '취소', value: 'CANCEL' },
        { title: '조정', value: 'ADJUSTMENT' },
      ],
      statuses: [
        { title: '전체', value: '' },
        { title: '정산대기', value: 'PENDING' },
        { title: '확정', value: 'CONFIRMED' },
        { title: '지급완료', value: 'PAID' },
        { title: '취소', value: 'CANCELED' },
      ],
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getSettlementList({
          ...this.search,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = {
        keyword: '',
        partyType: '',
        settlementType: '',
        settlementStatus: '',
        startDate: monthAgoText(),
        endDate: todayText(),
      }
      this.page = 1
      this.fetchList()
    },
    formatDateTime(value) {
      return value ? String(value).replace('T', ' ').substring(0, 16) : '-'
    },
    formatAmount(value) {
      if (value == null || value === '') return '-'
      return `${Number(value).toLocaleString()}원`
    },
    paymentText(value) {
      return { CASH: '계좌이체', CARD: '카드결제' }[value] || '-'
    },
    partyText(value) {
      return { HQ: '본사', BRANCH: '지점', CONSIGNOR: '위탁자', PG: 'PG사', DELIVERY: '배송사' }[value] || value || '-'
    },
    typeText(value) {
      return {
        PRODUCT_SALE: '상품 판매금',
        PROCESSING_FEE: '임가공비',
        SHIPPING_FEE: '배송비',
        CARD_FEE: '카드 수수료',
        BRANCH_COMMISSION: '지점 수수료',
        CONSIGNMENT_PAYABLE: '위탁 정산금',
        REFUND: '환불',
        CANCEL: '취소',
        ADJUSTMENT: '조정',
      }[value] || value || '-'
    },
    statusText(value) {
      return { PENDING: '정산대기', CONFIRMED: '확정', PAID: '지급완료', CANCELED: '취소' }[value] || value || '-'
    },
    statusColor(value) {
      return { PENDING: 'grey', CONFIRMED: 'blue', PAID: 'success', CANCELED: 'error' }[value] || 'grey'
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <div>
        <h1 class="page-title">정산 관리</h1>
        <p class="page-desc">상품 판매금, 임가공비, 배송비, 카드 수수료, 환불/취소 정산 원장을 조회합니다.</p>
      </div>
    </div>

    <section class="search-panel">
      <div class="search-grid">
        <v-text-field v-model="search.startDate" type="date" label="시작일" variant="outlined" density="compact" hide-details />
        <v-text-field v-model="search.endDate" type="date" label="종료일" variant="outlined" density="compact" hide-details />
        <v-select v-model="search.partyType" :items="partyTypes" label="정산 대상" variant="outlined" density="compact" hide-details />
        <v-select v-model="search.settlementType" :items="settlementTypes" label="정산 항목" variant="outlined" density="compact" hide-details />
        <v-select v-model="search.settlementStatus" :items="statuses" label="상태" variant="outlined" density="compact" hide-details />
        <v-text-field
          v-model="search.keyword"
          placeholder="주문번호, 상품명, 지점명, 회원명"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          @keyup.enter="handleSearch"
        />
        <div class="search-actions">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </div>
    </section>

    <section class="list-panel">
      <div class="list-title">
        <span>정산 원장 목록</span>
        <v-chip size="small" color="primary" variant="tonal">{{ total }}건</v-chip>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table class="dark-table" density="compact">
        <thead>
          <tr>
            <th>발생일시</th>
            <th>주문번호</th>
            <th>상품</th>
            <th>판매지점</th>
            <th>정산대상</th>
            <th>항목</th>
            <th>결제</th>
            <th class="text-right">총액</th>
            <th class="text-right">수수료</th>
            <th class="text-right">정산액</th>
            <th>상태</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.settlementLedgerCode">
            <td>{{ formatDateTime(item.occurredDt) }}</td>
            <td>{{ item.orderCode || '-' }}</td>
            <td>
              <strong>{{ item.productName || '-' }}</strong>
              <div v-if="item.memo" class="muted">{{ item.memo }}</div>
            </td>
            <td>{{ item.branchName || '-' }}</td>
            <td>{{ partyText(item.partyType) }}<span v-if="item.partyBranchName"> · {{ item.partyBranchName }}</span><span v-if="item.partyUserName"> · {{ item.partyUserName }}</span></td>
            <td>{{ typeText(item.settlementType) }}</td>
            <td>{{ paymentText(item.paymentType) }}</td>
            <td class="text-right">{{ formatAmount(item.grossAmount) }}</td>
            <td class="text-right">{{ formatAmount(item.feeAmount || item.cardFeeAmount) }}</td>
            <td class="text-right strong">{{ formatAmount(item.netAmount) }}</td>
            <td><v-chip size="small" :color="statusColor(item.settlementStatus)" variant="tonal">{{ statusText(item.settlementStatus) }}</v-chip></td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="11" class="empty-cell">정산 원장이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
    </section>
  </div>
</template>

<style scoped>
.page-title { font-size: 24px; font-weight: 800; margin: 0; color: #f8fafc; }
.page-desc { margin: 6px 0 0; color: #a9b6c9; font-size: 13px; }
.search-panel, .list-panel { border: 1px solid #334155; background: #1c222e; border-radius: 8px; padding: 16px; }
.search-panel { margin-bottom: 16px; }
.search-grid { display: grid; grid-template-columns: 150px 150px 160px 180px 150px minmax(260px, 1fr) auto; gap: 10px; align-items: center; }
.search-actions { display: flex; gap: 8px; justify-content: flex-end; }
.list-title { display: flex; align-items: center; gap: 8px; font-size: 17px; font-weight: 800; color: #f8fafc; margin-bottom: 12px; }
.dark-table { border: 1px solid #334155; border-radius: 8px; overflow: hidden; background: #111827; }
.dark-table :deep(th) { background: #252d3b !important; color: #dbeafe !important; font-weight: 800 !important; white-space: nowrap; }
.dark-table :deep(td) { color: #e5e7eb !important; border-bottom: 1px solid #243041 !important; vertical-align: middle; }
.muted { margin-top: 3px; color: #94a3b8; font-size: 12px; }
.strong { font-weight: 800; }
.empty-cell { text-align: center; color: #94a3b8 !important; padding: 36px !important; }
@media (max-width: 1280px) {
  .search-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .search-actions { justify-content: flex-start; }
}
</style>
